import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Apply = () => {
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', business_name: '',
    monthly_revenue: '', loan_amount: '', contact_method: 'phone', contact_time: 'morning', message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
        <div className="max-w-md text-center animate-fade-in-up">
          <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-[#115E59]" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Application Received
          </h1>
          <p className="text-slate-500 mb-8">
            Thank you, {form.full_name}. Our team will review your application and reach out within one business day.
          </p>
          <Link to="/" className="text-sm font-semibold text-[#115E59] hover:underline">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-10">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        <div className="mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#115E59] mb-4 block">Loan Application</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            Apply for Financing
          </h1>
          <p className="text-slate-500 max-w-lg">
            Fill out the form below and our team will follow up within one business day.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="loan-application-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Full Name *</label>
              <input data-testid="apply-full-name" name="full_name" required value={form.full_name} onChange={handleChange}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all" placeholder="John Smith" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Email *</label>
              <input data-testid="apply-email" name="email" type="email" required value={form.email} onChange={handleChange}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all" placeholder="john@company.com" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Phone *</label>
              <input data-testid="apply-phone" name="phone" type="tel" required value={form.phone} onChange={handleChange}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all" placeholder="(555) 123-4567" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Business Name *</label>
              <input data-testid="apply-business-name" name="business_name" required value={form.business_name} onChange={handleChange}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all" placeholder="Your Business LLC" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Monthly Revenue</label>
              <input data-testid="apply-revenue" name="monthly_revenue" value={form.monthly_revenue} onChange={handleChange}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all" placeholder="$25,000" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Desired Loan Amount</label>
              <input data-testid="apply-loan-amount" name="loan_amount" value={form.loan_amount} onChange={handleChange}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all" placeholder="$100,000" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Best Way to Contact</label>
              <select data-testid="apply-contact-method" name="contact_method" value={form.contact_method} onChange={handleChange}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all">
                <option value="phone">Phone</option>
                <option value="email">Email</option>
                <option value="text">Text Message</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Best Time to Contact</label>
              <select data-testid="apply-contact-time" name="contact_time" value={form.contact_time} onChange={handleChange}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all">
                <option value="morning">Morning (9am to 12pm)</option>
                <option value="afternoon">Afternoon (12pm to 3pm)</option>
                <option value="evening">Late Afternoon (3pm to 5pm)</option>
                <option value="anytime">Anytime</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Additional Notes</label>
            <textarea data-testid="apply-message" name="message" rows={3} value={form.message} onChange={handleChange}
              className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all resize-none" placeholder="Anything else we should know..." />
          </div>

          {error && <div data-testid="apply-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">{error}</div>}

          <button
            data-testid="apply-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-slate-900 text-white font-semibold text-sm py-4 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Apply;
